import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SchedulePicker from './SchedulePicker';
import type { Schedule } from './SchedulePicker';
import { Tooltip } from '../Tooltip';
import { getLocalTimezoneAbbr } from '../../utils/formatters';
import { ui } from '../../config/uiSizes';

interface ScheduleSectionProps {
    type: 'snapshot' | 'migration';
    isRecurringEnabled: boolean;
    onToggle: (enabled: boolean) => void;
    schedule: Schedule;
    onScheduleChange: (schedule: Schedule) => void;
    cronExpression: string;
    scheduleDisplay: string;
}

const ScheduleSection: React.FC<ScheduleSectionProps> = ({
    type,
    isRecurringEnabled,
    onToggle,
    schedule,
    onScheduleChange,
    scheduleDisplay
}) => {
    const labels = {
        snapshot: {
            title: 'Make This a Recurring Snapshot',
            tooltip: '(Optional) Set up a schedule for automatic recurring snapshots to ensure your data is regularly backed up without manual intervention.',
            dayLabel: 'Select day(s) to run snapshot',
            timeLabel: 'Select time to run snapshot',
            ariaLabel: 'Enable recurring snapshots'
        },
        migration: {
            title: 'Make This a Recurring Migration',
            tooltip: '(Optional) Set up a schedule for automatic recurring migrations to ensure your migrations run regularly without manual intervention.',
            dayLabel: 'Select day(s) to run migration',
            timeLabel: 'Select time to run migration',
            ariaLabel: 'Enable recurring migrations'
        }
    };

    const label = labels[type];

    return (
        <div className="pt-6 border-t-2 border-gray-300 dark:border-gray-600">
            <div
                className={`flex lg:flex-col xl:flex-row items-center lg:items-start xl:items-center justify-between lg:gap-2 xl:gap-0 ${ui.mb.title}`}
            >
                <div className={`flex items-center ${ui.gap.sm}`}>
                    <h3
                        className={`${ui.text.formStepTitle} font-extrabold text-gray-800 dark:text-gray-100`}
                    >
                        {label.title}
                    </h3>
                    <Tooltip
                        content={label.tooltip}
                        position="right"
                        className={`${ui.text.formSectionLabel} font-semibold`}
                    />
                </div>
                <button
                    type="button"
                    onClick={() => onToggle(!isRecurringEnabled)}
                    className={`lg:self-end xl:self-auto relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isRecurringEnabled ? 'bg-green-600 dark:bg-green-700' : 'bg-gray-400 dark:bg-gray-700'
                        }`}
                    role="switch"
                    aria-checked={isRecurringEnabled}
                    aria-label={label.ariaLabel}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isRecurringEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                    />
                </button>
            </div>

            <AnimatePresence>
                {isRecurringEnabled && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1]
                        }}
                        className="overflow-hidden"
                    >
                        <div className="space-y-5 mt-4">
                            <div
                                className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding}`}
                            >
                                <div className={`flex items-center justify-between ${ui.mb.sm}`}>
                                    <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                                        <svg
                                            className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <span
                                            className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200`}
                                        >
                                            {label.dayLabel}
                                        </span>
                                    </div>
                                    <span
                                        className={`${ui.text.xs} font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full`}
                                    >
                                        {schedule.days.length} day
                                        {schedule.days.length !== 1 ? 's' : ''} selected
                                    </span>
                                </div>
                                <SchedulePicker
                                    value={schedule}
                                    onChange={onScheduleChange}
                                    showTime={false}
                                />

                                <div className="border-t border-blue-200 dark:border-blue-700 my-4"></div>

                                {/* Time Picker */}
                                <div className={`flex items-center ${ui.gap.sm} mb-3`}>
                                    <svg
                                        className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span
                                        className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200`}
                                    >
                                        {label.timeLabel}
                                    </span>
                                    <span className={`${ui.text.xs} text-gray-400 dark:text-gray-500 font-normal ml-1`}>
                                        ({getLocalTimezoneAbbr()})
                                    </span>
                                </div>
                                <div
                                    className={`flex items-center ${ui.gap.sm} bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-2 w-fit`}
                                >
                                    <select
                                        aria-label="Hour"
                                        value={schedule.hour ?? ''}
                                        onChange={(e) =>
                                            onScheduleChange({
                                                ...schedule,
                                                hour: e.target.value === '' ? null : Number(e.target.value)
                                            })
                                        }
                                        className={`w-14 h-8 text-center ${ui.text.label} rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500`}
                                    >
                                        <option value="">HH</option>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                            <option key={h} value={h}>
                                                {h}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="text-lg text-gray-400 dark:text-gray-500 font-bold">:</span>
                                    <select
                                        aria-label="Minute"
                                        value={schedule.minute ?? ''}
                                        onChange={(e) =>
                                            onScheduleChange({
                                                ...schedule,
                                                minute: e.target.value === '' ? null : Number(e.target.value)
                                            })
                                        }
                                        className={`w-14 h-8 text-center ${ui.text.label} rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500`}
                                    >
                                        <option value="">MM</option>
                                        {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                                            <option key={m} value={m}>
                                                {String(m).padStart(2, '0')}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="flex rounded overflow-hidden ml-1">
                                        <button
                                            type="button"
                                            onClick={() => onScheduleChange({ ...schedule, ampm: 'AM' })}
                                            className={`${ui.button.sm} h-8 font-semibold transition ${schedule.ampm === 'AM'
                                                    ? 'bg-blue-600 dark:bg-blue-700 text-white'
                                                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            AM
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => onScheduleChange({ ...schedule, ampm: 'PM' })}
                                            className={`${ui.button.sm} h-8 font-semibold transition ${schedule.ampm === 'PM'
                                                    ? 'bg-blue-600 dark:bg-blue-700 text-white'
                                                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                                }`}
                                        >
                                            PM
                                        </button>
                                    </div>
                                </div>
                                <p
                                    className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1`}
                                >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    All times are shown in your local timezone
                                </p>
                            </div>

                            {/* Schedule Summary */}
                            <div
                                className={`bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg ${ui.page.smPadding}`}
                            >
                                <h4
                                    className={`${ui.text.label} font-bold text-blue-900 dark:text-blue-200 mb-3 flex items-center ${ui.gap.xs}`}
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                            fillRule="evenodd"
                                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Schedule Summary
                                </h4>
                                <div className="border-t border-blue-200 dark:border-blue-700 mb-1"></div>
                                <div className={`space-y-3 ${ui.text.label}`}>
                                    <div>
                                        <span className="text-gray-600 dark:text-gray-400 font-medium">
                                            Full Schedule:
                                        </span>
                                        <p className="text-gray-900 dark:text-gray-100 font-semibold mt-1">
                                            {scheduleDisplay ? (
                                                scheduleDisplay
                                            ) : (
                                                <span className="text-gray-400 dark:text-gray-500 italic">
                                                    No schedule set
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ScheduleSection;
